
import Vue from 'vue';
import { Runnit, TeamMember } from '@run-diffusion/shared';
import { get$bindFirestoreOptions, TeamUserMixin } from '@/mixins';
import { db, Query } from '@/firebase';
import { mapActions, mapState } from 'vuex';
import { formatTruncatedEmailForChip } from '@/utils/utils';

export default Vue.extend({
	name: 'RunnitBoardOwnerTableCell',
	mixins: [
		TeamUserMixin,
	],
	props: {
		runnit: { type: Object as () => Runnit, required: true },
		// The board is gone (deleted since the list loaded): show the owner, but open nothing.
		disabled: { type: Boolean, default: false },
	},
	data () {
		return {
			ownerTeamMembers: [] as TeamMember[],
			loadingOwner: false,
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		ownerTeamMember (): TeamMember | null {
			return this.ownerTeamMembers?.[0] || null;
		},
		canChangeOwner (): boolean {
			return !this.disabled && this.canChangeRunnitOwner(this.runnit);
		},
		/** An owner still on the team, shown as a chip. Featured templates and the caller's own read as text. */
		showsOwnerChip (): boolean {
			return !!(
				!this.runnit.isFeatured &&
				this.runnit.userId !== this._get(this.user, 'id') &&
				this.ownerTeamMember
			);
		},
		ownerText (): string {
			if (this.runnit.isFeatured) return 'RunDiffusion';
			if (this.runnit.userId === this._get(this.user, 'id')) return 'You';
			return 'No owner';
		},
	},
	watch: {
		runnit: {
			immediate: true,
			handler (newVal: Runnit, oldVal: Runnit) {
				const newRunnitId: string = newVal?.id || null;
				const oldRunnitId: string = oldVal?.id || null;
				const newOwnerId: string = newVal?.userId || null;
				const oldOwnerId: string = oldVal?.userId || null;
				const newTeamId: string = newVal?.teamId || null;
				const oldTeamId: string = oldVal?.teamId || null;

				if (newRunnitId !== oldRunnitId || newOwnerId !== oldOwnerId || newTeamId !== oldTeamId) {
					this.loadOwnerTeamMember(newTeamId, newOwnerId);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
		]),
		formatTruncatedEmailForChip,
		onOwnerClick (event: Event) {
			// Anyone else's click falls through to the row, which opens the board
			if (!this.canChangeOwner) return;
			event.stopPropagation();
			this.updateRunnitState({
				runnitManageAccessOpen: true,
				runnit: this.runnit,
			});
		},
		async loadOwnerTeamMember (teamId: string, ownerId: string) {
			this.ownerTeamMembers = [];

			// Don't load if it's a personal runnit (no teamId) or no ownerId
			if (!teamId || !ownerId) return;

			this.loadingOwner = true;

			try {
				const ownerTeamMemberRef: Query = db.collection(`teams/${teamId}/teamMembers`)
					.where('userId', '==', ownerId)
					.where('isAccepted', '==', true)
					.limit(1);

				await this.$bind(
					'ownerTeamMembers',
					ownerTeamMemberRef,
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error loading owner team member:', error);
			} finally {
				this.loadingOwner = false;
			}
		},
	},
});
